import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

const MIN_LENGTH = 10;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearFlag = useAuthStore((s) => s.clearPasswordResetRequired);
  const forced = user?.passwordResetRequired ?? false;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must differ from current password');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/me/password', {
        currentPassword,
        newPassword,
      });
      clearFlag();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (forced) {
        navigate('/', { replace: true });
      } else {
        setSuccess('Password updated.');
      }
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        if (status === 401) {
          setError('Current password is incorrect.');
        } else if (detail) {
          setError(detail);
        } else {
          setError('Could not change password. Please try again.');
        }
      } else {
        setError('Could not change password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg p-6">
      <h1 className="mb-2 text-2xl font-semibold text-primary">
        {forced ? 'Set a new password' : 'Change password'}
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        {forced
          ? 'This account was provisioned with a temporary password. Please choose a new password before continuing.'
          : `Minimum ${MIN_LENGTH} characters. Choose something you have not used before.`}
      </p>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="change-password-form">
        <div className="space-y-2">
          <label htmlFor="current" className="text-sm font-medium text-foreground">
            Current password
          </label>
          <Input
            id="current"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="new" className="text-sm font-medium text-foreground">
            New password
          </label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="confirm" className="text-sm font-medium text-foreground">
            Confirm new password
          </label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-md border border-emerald-600/50 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200"
          >
            {success}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Saving…' : 'Update password'}
        </Button>
      </form>
    </div>
  );
}
